/**
 * 天地图工具类
 * 用于调用天地图API进行逆地理编码和地点搜索
 * 
 * 配置说明：
 * 1. 在项目根目录创建 .env 文件（如果不存在）
 * 2. 添加配置：VUE_APP_TIANDITU_KEY=你的天地图API Key
 * 3. 天地图API Key申请地址：https://www.tianditu.gov.cn/
 * 
 * 注意：如果没有配置环境变量，将使用默认Key（可能有限制）
 */

// 天地图API Key（优先从环境变量获取，如果没有则使用默认值）
// 建议在 .env 文件中配置：VUE_APP_TIANDITU_KEY=你的Key
const TIANDITU_KEY = process.env.VUE_APP_TIANDITU_KEY || 'f1d9eced233438796aa8c612693d7a52'
const TIANDITU_BASE_URL = 'https://api.tianditu.gov.cn'

/**
 * 逆地理编码（根据经纬度获取地址）
 * @param {number} longitude - 经度
 * @param {number} latitude - 纬度
 * @returns {Promise<{address: string, longitude: number, latitude: number, city?: string, province?: string, district?: string}>}
 */
export async function reverseGeocode(longitude, latitude) {
  if (!longitude || !latitude) {
    throw new Error('经纬度参数不能为空')
  }

  if (!TIANDITU_KEY) {
    throw new Error('天地图API Key未配置')
  }

  try {
    // 构建请求参数 - 天地图逆地理编码API格式
    const postData = {
      lon: longitude,
      lat: latitude,
      ver: 1
    }

    const postStr = JSON.stringify(postData)
    // 注意：postStr需要URL编码，但不需要对+号进行特殊处理
    const encodedPostStr = encodeURIComponent(postStr)
    const encodedKey = encodeURIComponent(TIANDITU_KEY)

    // 构建请求URL - 天地图逆地理编码API
    // 格式：https://api.tianditu.gov.cn/geocoder?postStr={postStr}&type=geocode&tk={key}
    const url = `${TIANDITU_BASE_URL}/geocoder?postStr=${encodedPostStr}&type=geocode&tk=${encodedKey}`

    console.log('逆地理编码请求:', { 
      longitude, 
      latitude, 
      postData,
      url: url.substring(0, 100) + '...' // 只显示URL前100个字符，避免日志过长
    })

    // 发送请求
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'User-Agent': 'Mozilla/5.0'
      }
    })

    if (!response.ok) {
      const errorText = await response.text()
      console.error('逆地理编码HTTP错误:', response.status, errorText)
      throw new Error(`地图服务请求失败: ${response.status}`)
    }

    let data
    try {
      data = await response.json()
    } catch (parseError) {
      const text = await response.text()
      console.error('逆地理编码响应解析失败:', parseError, '响应文本:', text)
      throw new Error('地图服务响应格式错误')
    }
    
    console.log('逆地理编码响应:', data)

    // 检查响应状态 - 天地图API可能返回status或statuscode字段
    // status: 0表示成功，其他值表示失败
    // statuscode: '0'表示成功
    const status = data.status
    const statuscode = data.statuscode
    const msg = data.msg || data.message || ''

    // 如果status不为0且不为undefined，或者statuscode不为'0'且不为0，说明有错误
    // 但有些情况下即使status不为0，也可能有部分数据，所以先尝试提取数据
    const hasError = (status !== undefined && status !== 0 && status !== '0') || 
                     (statuscode !== undefined && statuscode !== 0 && statuscode !== '0')

    // 获取结果数据 - 尝试多种可能的字段名
    let result = data.result || data.location || data.addressComponent || data
    
    // 如果明确有错误且没有结果数据，才抛出错误
    if (hasError && !result && !data.formatted_address && !data.address) {
      const errorMsg = msg || '未知错误'
      console.error('逆地理编码业务错误:', { status, statuscode, msg, data })
      throw new Error(`地图服务返回错误: ${errorMsg} (status: ${status || statuscode})`)
    }

    // 如果result为空，尝试使用整个data对象
    if (!result || (typeof result === 'object' && Object.keys(result).length === 0)) {
      result = data
    }

    // 提取地址信息
    let address = result.formatted_address || result.address || ''

    // 如果没有formatted_address和address，尝试从addressComponent构建
    if (!address && result.addressComponent) {
      const addrComp = result.addressComponent
      const parts = []
      if (addrComp.province) parts.push(addrComp.province)
      if (addrComp.city) parts.push(addrComp.city)
      if (addrComp.district) parts.push(addrComp.district)
      if (addrComp.street) parts.push(addrComp.street)
      if (addrComp.streetNumber) parts.push(addrComp.streetNumber)
      address = parts.join('')
    }

    // 如果还是没有地址，尝试从其他字段获取
    if (!address) {
      // 尝试从location字段获取
      if (result.location && result.location.address) {
        address = result.location.address
      }
      // 尝试从pois字段获取（如果有POI信息）
      if (!address && result.pois && result.pois.length > 0) {
        const poi = result.pois[0]
        address = poi.address || poi.name || ''
      }
    }

    const responseData = {
      address: address || '',
      longitude,
      latitude,
      addressComponent: result.addressComponent || result.addressComponent
    }

    // 提取城市信息
    if (result.addressComponent) {
      const addrComp = result.addressComponent
      if (addrComp.city) {
        responseData.city = addrComp.city.replace('市', '').replace('省', '').trim()
        responseData.cityFull = addrComp.city
      }
      if (addrComp.province) {
        responseData.province = addrComp.province
      }
      if (addrComp.district) {
        responseData.district = addrComp.district
      }
    }

    console.log('逆地理编码成功:', responseData)
    return responseData
  } catch (error) {
    console.error('逆地理编码失败:', {
      longitude,
      latitude,
      error: error.message,
      stack: error.stack
    })
    throw error
  }
}

/**
 * 地点搜索
 * @param {string} keyword - 搜索关键词
 * @param {Object} options - 搜索选项
 * @param {string} options.city - 城市名称，用于限制搜索范围
 * @param {string} options.mapBound - 地图边界，格式为"minx,miny,maxx,maxy"
 * @param {number} options.level - 地图级别，范围1-18，默认12
 * @param {number} options.queryType - 查询类型，1=普通搜索，2=视野内搜索，7=地名搜索
 * @param {number} options.start - 起始位置，默认0
 * @param {number} options.count - 返回数量，默认10
 * @returns {Promise<Array<{name: string, address: string, fullAddress: string, longitude: number, latitude: number}>>}
 */
export async function searchLocation(keyword, options = {}) {
  if (!keyword || !keyword.trim()) {
    throw new Error('搜索关键词不能为空')
  }

  if (!TIANDITU_KEY) {
    throw new Error('天地图API Key未配置')
  }

  try {
    // 构建搜索关键词
    let searchKeyword = keyword.trim()
    if (options.city && !searchKeyword.includes(options.city)) {
      searchKeyword = options.city + searchKeyword
    }

    // 构建搜索参数
    const searchParams = {
      keyWord: searchKeyword,
      mapBound: options.mapBound || '73.66,18.15,135.05,53.55', // 中国范围
      level: options.level || 12,
      queryType: options.queryType || 1,
      start: options.start || 0,
      count: options.count || 10
    }

    const postStr = JSON.stringify(searchParams)
    const encodedPostStr = encodeURIComponent(postStr).replace(/\+/g, '%20')
    const encodedKey = encodeURIComponent(TIANDITU_KEY).replace(/\+/g, '%20')

    // 构建请求URL
    const url = `${TIANDITU_BASE_URL}/v2/search?postStr=${encodedPostStr}&type=query&tk=${encodedKey}`

    // 发送请求
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'User-Agent': 'Mozilla/5.0'
      }
    })

    if (!response.ok) {
      throw new Error(`地图服务请求失败: ${response.status}`)
    }

    const data = await response.json()

    // 检查响应状态
    const pois = data.pois || data.data || []
    if (!pois || pois.length === 0) {
      return []
    }

    // 转换数据格式
    const results = pois.map(poi => {
      // 提取经纬度
      let longitude = null
      let latitude = null

      // 尝试从lonlat字段解析（格式为"lon,lat"）
      if (poi.lonlat) {
        const coords = poi.lonlat.split(',')
        if (coords.length === 2) {
          longitude = parseFloat(coords[0].trim())
          latitude = parseFloat(coords[1].trim())
        }
      }

      // 如果没有从lonlat解析到，尝试其他字段
      if (longitude === null || latitude === null) {
        longitude = poi.lon || poi.longitude
        latitude = poi.lat || poi.latitude
      }

      const name = poi.name || ''
      const address = poi.address || poi.addr || ''
      const fullAddress = address ? `${name} ${address}`.trim() : name

      return {
        name,
        address,
        fullAddress,
        longitude: longitude ? parseFloat(longitude) : null,
        latitude: latitude ? parseFloat(latitude) : null
      }
    })

    return results
  } catch (error) {
    console.error('地点搜索失败:', error)
    throw error
  }
}

/**
 * 获取浏览器当前位置
 * @returns {Promise<{longitude: number, latitude: number}>}
 */
export function getCurrentLocation() {
  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      reject(new Error('浏览器不支持定位功能'))
      return
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        resolve({
          longitude: position.coords.longitude,
          latitude: position.coords.latitude
        })
      },
      (error) => {
        let errorMsg = '定位失败'
        switch (error.code) {
          case error.PERMISSION_DENIED:
            errorMsg = '定位权限被拒绝'
            break
          case error.POSITION_UNAVAILABLE:
            errorMsg = '定位信息不可用'
            break
          case error.TIMEOUT:
            errorMsg = '定位超时'
            break
          default:
            errorMsg = '定位失败'
            break
        }
        reject(new Error(errorMsg))
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 0
      }
    )
  })
}

